import { Box, Container, styled } from '@mui/material';
import { get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Components
import TabComponent from 'src/components/Tab/TabComponent';
import AddressesTab from 'src/features/CompanyDetail/Addresses';
import AlertsTab from 'src/features/CompanyDetail/Alerts';
import ComingSoon from 'src/features/CompanyDetail/Common/ComingSoon';
import CompanyInformation from 'src/features/CompanyDetail/Common/CompanyInformation';
import FormContributeCompany from 'src/features/CompanyDetail/Common/FormContributeCompany';
import FormReportWrong from 'src/features/CompanyDetail/Common/FormReportWrong';
import FormReview from 'src/features/CompanyDetail/Common/FormReview';
// import ContactsTab from 'src/features/CompanyDetail/Contacts';
import ContactsByAITab from 'src/features/CompanyDetail/ContactsByAI';
import NegativeMediaTab from 'src/features/CompanyDetail/NegativeMedia';
import OverViewTab from 'src/features/CompanyDetail/OverView';
import Reviews from 'src/features/CompanyDetail/Reviews';
// import SocialMediaTab from 'src/features/CompanyDetail/SocialMedia';
import SocialMediaByAITab from 'src/features/CompanyDetail/SocialMediaByAI';
import StockPlaceTab from 'src/features/CompanyDetail/StockPlace';
import TradeMarkTab from 'src/features/CompanyDetail/TradeMark';
import WebDetailTab from 'src/features/CompanyDetail/WebDetail';

// Utilities
import dayjs from 'dayjs';
import analytics from 'src/analytics';
import SupplyChainMapPlaceHolder from 'src/asset/images/company/SupplyChainMap.png';
import TradingAreaPlaceHolder from 'src/asset/images/company/TradingArea.png';
import BreadcrumbWithBtnBack from 'src/components/BreadcrumbWithBtnBack';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import CompanyTradeDataBillsOfLading from 'src/features/CompanyDetail/BillOfLanding';
import { TypeContributeCompany } from 'src/features/CompanyDetail/Common/config';
import FormReportMergeProfile from 'src/features/CompanyDetail/Common/FormReportMergeProfile';
import CompanyAggregatedByBOL from 'src/features/CompanyDetail/CompanyAggregatedByBOL';
import Competitor from 'src/features/CompanyDetail/Competitor';
import { TABS_COMPANY, TypeTabCompany } from 'src/features/CompanyDetail/config';
import HSCode from 'src/features/CompanyDetail/HSCode';
import LoadingByCheckIsExistTab from 'src/features/CompanyDetail/LoadingByCheckIsExistTab';
import MarketAnalytics from 'src/features/CompanyDetail/MarketAnalytics';
import SkeletonOverview from 'src/features/CompanyDetail/OverView/SkeletonOverview';
import CompanyTradeDataViewByPorts from 'src/features/CompanyDetail/ViewByPorts';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useGetPointsFeature } from 'src/hooks/useGetPointsFeature';
import { useQueryParams } from 'src/hooks/useQueryParam';
import useRefetch from 'src/hooks/useRefetch';
import { useMutateCheckEntityPaid } from 'src/services/mutate';
import {
  QUERY_KEY_COMPANY_REVIEW,
  QUERY_KEY_COMPANY_REVIEW_DETAIL,
  QUERY_KEY_USAGE,
  usePreloadCompanyDetail,
  useQueryCheckCompanyTabIsExist,
  useQueryUsageInformation,
} from 'src/services/queries';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { EmptyText, FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { TypeBuyerSupplier } from 'src/utils/constants/enum';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { ERROR_CODE } from 'src/utils/constants/statusCode';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { useCompanyContribute } from 'src/zustand/useCompanyContribute';
import { NotifyType, useNotifyCreditCosts } from 'src/zustand/useNotifyCreditCosts';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const CompanyDetail = () => {
  const queryParams = useQueryParams();
  const currentTab = queryParams['company_detail_tab'] || TypeTabCompany.Overview;
  /** acceptGetDetail values:
   * _ Initial value: null
   * _ When we accept to get data: true
   * _ After fetch data successfully: done
   */
  const [acceptGetDetail, setAcceptGetDetail] = useState<string | boolean | null>(null);
  const { handleOpenNotify } = useNotifyCreditCosts((state) => state);
  const { id = '' } = useParams();
  const {
    isFetching: isPreloadFetching,
    overview,
    address,
    socialMedia,
    review,
    contact,
    socialReview,
    reviewRelatedQuestion,
    reviewDetail,
    overviewFallback,
    stock,
    webDetail,
  } = usePreloadCompanyDetail({ id: id });

  const { data: dataCompanyWebDetail, isFetching: isFetchingWebDetail } = webDetail;
  const { data, isFetching } = overview;
  const { data: dataFallback } = overviewFallback;
  // const { data, isFetching, refetch } = useQueryCompanyDetail(id, acceptGetDetail === true);
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();

  const { data: dataUsage } = useQueryUsageInformation();
  const { mutateAsync } = useMutateCheckEntityPaid();
  const { refetchQuery } = useRefetch();
  const { additionalData, open, type, handleCloseContribute } = useCompanyContribute((state) => state);
  const navigate = useNavigate();
  const location = useLocation();
  const pointsView = useGetPointsFeature(LIST_FEATURES.COMPANY_PROFILE_COMPANY_OVERVIEW, LIST_ACTIONS.VIEW, dataUsage);
  const hasHistoryEntryExist = location.key !== 'default'; // false => chuyển từ 1 trang ngoài Adamftd qua\
  const { data: dataCheckCompanyIsExist, isFetching: isFetchingCheckCompanyTabIsExist } =
    useQueryCheckCompanyTabIsExist({
      company_id: id,
      params: {
        company_id: id,
        start_date:
          queryParams?.start_date ??
          dayjs(runtimeConfig?.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        end_date:
          queryParams?.end_date ??
          dayjs(runtimeConfig?.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        isBuyer: true,
      },
    });

  useEffect(() => {
    setAcceptGetDetail(true);
  }, []);

  useEffect(() => {
    if (!data?.data?.name) return;
    analytics.logEvent('page_view', {
      page_title: `Company Detail - ${data?.data?.name}`,
      page_location: window.location.href,
      page_path: window.location.pathname,
    });

    // get Usage after have data company
    refetchQuery([QUERY_KEY_USAGE]);
  }, [data?.data?.name]);

  // useEffect(() => {
  //   // After fetch data done
  //   if (isFetching === false) {
  //     setAcceptGetDetail('done');
  //   }
  // }, [isFetching]);
  // const companyDetailTab = useMemo(() => {
  //   if (isFetchingCheckCompanyTabIsExist)
  //     return map(TABS_COMPANY, (tab) => ({
  //       ...tab,
  //       ...{
  //         isLoading: tab.key === TypeTabCompany.Overview ? false : true,
  //       },
  //     }));
  //   return filter(TABS_COMPANY, ({ isExistByField }) => {
  //     return get(checkCompanyTabIsExistQuery.data.data, isExistByField || '') !== false;
  //   }).map((tab) => ({
  //     ...tab,
  //     ...{
  //       isLoading: false,
  //     },
  //   }));
  // }, [JSON.stringify(checkCompanyTabIsExistQuery)]);

  const { hasAccess } = useFeatureAccess();

  const tabs = useMemo(() => {
    if (isPreloadFetching) return TABS_COMPANY;

    return TABS_COMPANY.filter((tab) => {
      // 1. Feature access check & API existential checks
      if (!hasAccess(tab.endpoints) || get(dataCheckCompanyIsExist.data, tab.dataExistenceField || '') === false)
        return false;

      return true;
    });
  }, [
    isPreloadFetching,
    isFetchingCheckCompanyTabIsExist,
    isFetchingWebDetail,
    hasAccess,
    JSON.stringify(dataCheckCompanyIsExist.data),
    JSON.stringify(dataCompanyWebDetail.data),
    contact?.data?.data,
    socialMedia?.data?.data,
  ]);

  useEffect(() => {
    if (
      [
        ERROR_CODE.NO_HAVE_ANY_CREDIT,
        ERROR_CODE.NO_HAVE_ENOUGH_CREDIT,
        ERROR_CODE.REACH_LIMIT_CREDITS,
        ERROR_CODE.RUN_OUT_OF_CREDIT,
      ].includes(data?.status_code)
    ) {
      handleOpenNotify({
        type: NotifyType.NotEnoughCredit,
        showCloseIcon: true,
        cbClose: () => {
          if (hasHistoryEntryExist) {
            navigate(-1);
          } else {
            navigate(AuthRouteKeys.Home);
          }
        },
      });
      return;
    }
  }, [data?.status_code]);

  // useEffect(() => {
  //   // const skipAskPayCredit = localStorage.getItem(SKIP_ASK_PAY_CREDIT);
  //
  //   // Users haven't bought plans yet.
  //   // if (!isNull(acceptGetDetail) && !acceptGetDetail && isUndefined(pointsView?.planName)) {
  //   //   handleOpenNotify({
  //   //     type: NotifyType.NotEnoughCredit,
  //   //     showCloseIcon: true,
  //   //     cbClose: () => {
  //   //       if (hasHistoryEntryExist) {
  //   //         navigate(-1);
  //   //       } else {
  //   //         navigate(AuthRouteKeys.Home);
  //   //       }
  //   //     },
  //   //   });
  //   //   return;
  //   // }
  //
  //   // // Users don't want show popup confirm credit expenditures.
  //   // if (!isNull(acceptGetDetail) && !acceptGetDetail && (skipAskPayCredit || pointsView?.creditCosts === 0)) {
  //   //   setAcceptGetDetail(true);
  //   //   return;
  //   // }
  //
  //   // if (!isNull(acceptGetDetail) && !acceptGetDetail && pointsView?.creditCosts > 0) {
  //   //   handleOpenNotify({
  //   //     type: NotifyType.CreditCost,
  //   //     textBtn: 'View Profile Detail',
  //   //     creditCost: pointsView?.creditCosts,
  //   //     showCloseIcon: true,
  //   //     cbClose: () => {
  //   //       if (hasHistoryEntryExist) {
  //   //         navigate(-1);
  //   //       } else {
  //   //         navigate(AuthRouteKeys.Home);
  //   //       }
  //   //     },
  //   //     cbAction: () => {
  //   //       setAcceptGetDetail(true);
  //   //       handleCloseNotify();
  //   //     },
  //   //   });
  //   // }
  // }, [
  //   // acceptGetDetail,
  //   // pointsView?.planName,
  //   // pointsView?.creditCosts
  // ]);

  // const fetchCheckPaid = async () => {
  //   try {
  //     const response = await mutateAsync({
  //       entityId: id,
  //       type: TypeCheckEntityPaid.COMPANY,
  //     });
  //     setAcceptGetDetail(response?.check_entity_id_type_created_30_days_result || false);
  //   } catch (error) {
  //     setAcceptGetDetail(false);
  //   }
  // };

  useEffect(() => {
    if (!id) {
      navigate(AuthRouteKeys.Home);
    } else {
      // fetchCheckPaid();
    }
    // setAcceptGetDetail(null);
  }, [id]);

  const renderForm = () => {
    switch (currentTab) {
      case TypeTabCompany.Overview:
        return (
          <OverViewTab
            companyFallback={dataFallback?.data}
            company={data?.data}
            isFetching={isFetching}
            // statusFetch={data?.status_code} refetch={refetch}
          />
        );
      case TypeTabCompany.Contacts:
        return <ContactsByAITab contact={contact} />;
      case TypeTabCompany.SocialMedia:
        return <SocialMediaByAITab socialMedia={socialMedia} />;
      // return <SocialMediaTab socialMedia={socialMedia} />;
      case TypeTabCompany.Reviews:
        return (
          <Reviews
            reviewDetail={reviewDetail}
            reviewRelatedQuestion={reviewRelatedQuestion}
            socialReview={socialReview}
            review={review}
          />
        );
      case TypeTabCompany.NegativeMedia:
        return <NegativeMediaTab company={data?.data} />;
      case TypeTabCompany.Trademark:
        return <TradeMarkTab company={data} />;
      case TypeTabCompany.Addresses:
        return <AddressesTab address={address} />;
      case TypeTabCompany.WebDetails:
        return <WebDetailTab isFetching={isFetchingWebDetail} dataCompanyWebDetail={dataCompanyWebDetail} />;
      case TypeTabCompany.DataAnalytics:
        return <MarketAnalytics />;
      case TypeTabCompany.SupplyChainMap:
        return <ComingSoon image={SupplyChainMapPlaceHolder} />;
      case TypeTabCompany.BillsOfLading:
        return <CompanyTradeDataBillsOfLading company={data?.data} />;
      case TypeTabCompany.HSCode:
        return <HSCode company={data?.data} />;
      case TypeTabCompany.Customers:
        return <CompanyAggregatedByBOL company={data?.data} type={TypeBuyerSupplier.buyer} />;
      case TypeTabCompany.Suppliers:
        return <CompanyAggregatedByBOL company={data?.data} type={TypeBuyerSupplier.supplier} />;
      case TypeTabCompany.Competitors:
        return <Competitor company={data?.data} />;
      case TypeTabCompany.PortsOfDelivery:
        return <CompanyTradeDataViewByPorts selectedTab={TypeTabTradeData.PortDelivery} />;
      case TypeTabCompany.PortsOfOrigin:
        return <CompanyTradeDataViewByPorts selectedTab={TypeTabTradeData.PortOrigin} />;
      case TypeTabCompany.TradingArea:
        return <ComingSoon image={TradingAreaPlaceHolder} />;
      case TypeTabCompany.StockPlace:
        return <StockPlaceTab stock={stock} id={id} />;
      case TypeTabCompany.Alerts:
        return <AlertsTab />;
      default:
        return (
          <OverViewTab
            companyFallback={dataFallback?.data}
            company={data?.data}
            isFetching={isFetching}
            //  statusFetch={data?.status_code} refetch={refetch}
          />
        );
    }
  };

  const renderFormContribute = () => {
    switch (type) {
      case TypeContributeCompany.Review:
        return (
          <FormReview
            callback={() => {
              refetchQuery([QUERY_KEY_COMPANY_REVIEW]);
              refetchQuery([QUERY_KEY_COMPANY_REVIEW_DETAIL]);
            }}
            companyData={data?.data}
            open={true}
            handleClose={handleCloseContribute}
          />
        );
      case TypeContributeCompany.ReportWrongOtherContact:
        return (
          <FormReportWrong
            oldData={additionalData.oldData}
            companyData={data?.data}
            open={true}
            handleClose={handleCloseContribute}
          />
        );
      case TypeContributeCompany.MergeCompanyProfile:
        return (
          <FormReportMergeProfile
            mergeData={additionalData.mergeData}
            companyData={data?.data}
            open={true}
            handleClose={handleCloseContribute}
          />
        );
      default:
        return (
          <FormContributeCompany companyData={data?.data} open={true} type={type} handleClose={handleCloseContribute} />
        );
    }
  };

  if (
    !acceptGetDetail ||
    [
      ERROR_CODE.NO_HAVE_ANY_CREDIT,
      ERROR_CODE.NO_HAVE_ENOUGH_CREDIT,
      ERROR_CODE.REACH_LIMIT_CREDITS,
      ERROR_CODE.RUN_OUT_OF_CREDIT,
    ].includes(data?.status_code)
  ) {
    return (
      <StyledContainer maxWidth="xl">
        <SkeletonOverview />
      </StyledContainer>
    );
  }

  const renderContent = () => {
    if (isPreloadFetching) return <LoadingByCheckIsExistTab />;

    return (
      <>
        <SectionErrorBoundary resetKey="CompanyInformation" sectionName="CompanyInformation">
          <CompanyInformation companyFallback={dataFallback?.data} company={data?.data} isFetching={isFetching} />
        </SectionErrorBoundary>
        <TabComponent
          tabs={tabs}
          selected={currentTab}
          handleChange={(e, value) => {
            navigate({
              pathname: AuthRouteKeys.CompanyDetail.replace(':id?', id),
              search: handleQueryParamFromObject({ ...queryParams, ...{ company_detail_tab: value } }),
            });
          }}
        />
        <Box mt={3.75} />
        <SectionErrorBoundary resetKey={currentTab} sectionName={`CompanyDetail-${currentTab}`}>
          {renderForm()}
        </SectionErrorBoundary>
      </>
    );
  };

  return (
    <StyledContainer maxWidth="xl">
      {open && renderFormContribute()}
      <BreadcrumbWithBtnBack
        listItem={[
          {
            text: 'Companies',
          },
          {
            text: data?.data?.country || dataFallback?.data?.country_code || EmptyText,
          },
          {
            text: data?.data?.jurisdiction || dataFallback?.data?.jurisdiction || EmptyText,
          },
          {
            text: data?.data?.name || dataFallback?.data?.name || EmptyText,
          },
        ]}
      />
      {renderContent()}
    </StyledContainer>
  );
};

export default CompanyDetail;
